import { useState, useEffect } from "react";
import { Stack, Typography, Box, IconButton } from "@mui/material";
import { ContentCopy as CopyIcon } from "@mui/icons-material";
import { JsonSchema, JsonSchemaRoot } from "./types";
import AcxMainTextField from "components/UI/AcxMainTextField";
import {
    StringFieldsEditor,
    NumberIntegerFieldsEditor,
    TypeSelectorField,
} from "./TypeFieldEditors";
import { AddSchemaDialog } from "./AddSchemaDialog";
import { RenamePropertyDialog } from "./RenamePropertyDialog";
import { ArrayItemsEditor } from "./ArrayItemsEditor";
import { ObjectPropertiesEditor } from "./ObjectPropertiesEditor";
import {
    INITIAL_DIALOG_STATE,
    TYPE_SPECIFIC_PROPS,
    DialogState,
    SchemaPath,
} from "./constants";

interface SchemaDetailEditorProps {
    schema: JsonSchemaRoot;
    selectedPath: SchemaPath;
    onSchemaUpdate: (schema: JsonSchemaRoot) => void;
    addPropertyPath?: SchemaPath | null;
    onAddPropertyPathChange?: (path: SchemaPath | null) => void;
    addItemPath?: SchemaPath | null;
    onAddItemPathChange?: (path: SchemaPath | null) => void;
    isPathLocked?: (path: SchemaPath) => boolean;
}

function getSchemaAtPath(
    schema: JsonSchema,
    path: SchemaPath,
): JsonSchema | null {
    let current: any = schema;
    for (const segment of path) {
        if (segment === "items" && current.items) {
            current = current.items;
            continue;
        }
        if (typeof segment === "string" && current.properties?.[segment]) {
            current = current.properties[segment];
            continue;
        }
        return null;
    }
    return current;
}

function updateSchemaAtPath(
    schema: JsonSchemaRoot,
    path: SchemaPath,
    updater: (current: JsonSchema) => JsonSchema,
): JsonSchemaRoot {
    if (path.length === 0) {
        return updater(schema) as JsonSchemaRoot;
    }

    const newSchema = JSON.parse(JSON.stringify(schema));
    let current: any = newSchema;

    for (let i = 0; i < path.length - 1; i++) {
        const segment = path[i];
        if (segment === "items") {
            current = current.items;
            continue;
        }
        if (typeof segment === "string") {
            current = current.properties?.[segment];
            continue;
        }
        return newSchema;
    }

    const lastSegment = path[path.length - 1];
    if (lastSegment === "items") {
        current.items = updater(current.items);
    } else if (typeof lastSegment === "string") {
        if (!current.properties) {
            current.properties = {};
        }
        current.properties[lastSegment] = updater(
            current.properties[lastSegment],
        );
    }

    return newSchema;
}

export function SchemaDetailEditor({
    schema,
    selectedPath,
    onSchemaUpdate,
    addPropertyPath,
    onAddPropertyPathChange,
    addItemPath,
    onAddItemPathChange,
    isPathLocked = () => false,
}: SchemaDetailEditorProps) {
    const [openAddProperty, setOpenAddProperty] = useState(false);
    const [openAddItem, setOpenAddItem] = useState(false);
    const [openRenameProperty, setOpenRenameProperty] = useState(false);
    const [propertyToRename, setPropertyToRename] = useState<string>("");
    const [propertyDialogState, setPropertyDialogState] =
        useState<DialogState>(INITIAL_DIALOG_STATE);
    const [itemDialogState, setItemDialogState] =
        useState<DialogState>(INITIAL_DIALOG_STATE);

    const selectedSchema = getSchemaAtPath(schema, selectedPath);
    const isCurrentNodeLocked = isPathLocked(selectedPath);

    // Open add property dialog if triggered from tree
    useEffect(() => {
        if (
            addPropertyPath &&
            JSON.stringify(addPropertyPath) === JSON.stringify(selectedPath)
        ) {
            setOpenAddProperty(true);
            onAddPropertyPathChange?.(null);
        }
    }, [addPropertyPath, selectedPath, onAddPropertyPathChange]);

    // Open add item dialog if triggered from tree
    useEffect(() => {
        if (
            addItemPath &&
            JSON.stringify(addItemPath) === JSON.stringify(selectedPath)
        ) {
            setOpenAddItem(true);
            onAddItemPathChange?.(null);
        }
    }, [addItemPath, selectedPath, onAddItemPathChange]);

    if (!selectedSchema) {
        return (
            <Box>
                <Typography color="textSecondary">
                    Select a schema node from the tree to edit
                </Typography>
            </Box>
        );
    }

    const handleUpdate = (updates: Partial<JsonSchema>) => {
        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current) => ({
                ...current,
                ...updates,
            }),
        );
        onSchemaUpdate(updatedSchema);
    };

    const handleTypeChange = (newType: string) => {
        const oldType = selectedSchema.type as keyof typeof TYPE_SPECIFIC_PROPS;
        const propsToRemove = TYPE_SPECIFIC_PROPS[oldType] || [];

        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => {
                const updated = { ...current, type: newType };
                propsToRemove.forEach((prop: string) => {
                    delete updated[prop];
                });
                return updated;
            },
        );
        onSchemaUpdate(updatedSchema);
    };

    const handleAddProperty = (state: DialogState) => {
        const newProperty: JsonSchema = {
            type: state.type as any,
            ...(state.title && { title: state.title }),
            ...(state.description && { description: state.description }),
        };

        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => ({
                ...current,
                properties: {
                    ...(current.properties || {}),
                    [state.name]: newProperty,
                },
            }),
        );

        onSchemaUpdate(updatedSchema);
        setOpenAddProperty(false);
        setPropertyDialogState(INITIAL_DIALOG_STATE);
    };

    const handleRemoveProperty = (propertyName: string) => {
        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => {
                const { [propertyName]: removed, ...rest } =
                    current.properties || {};
                return {
                    ...current,
                    properties: rest,
                };
            },
        );
        onSchemaUpdate(updatedSchema);
    };

    const handleOpenRenameProperty = (propertyName: string) => {
        setPropertyToRename(propertyName);
        setOpenRenameProperty(true);
    };

    const handleRenameProperty = (newName: string) => {
        if (!propertyToRename || newName === propertyToRename) {
            return;
        }

        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => {
                const properties = current.properties || {};
                const propertySchema = properties[propertyToRename];

                // Check if new name already exists (safety check)
                if (properties[newName]) {
                    console.error(
                        `Property "${newName}" already exists. Rename operation cancelled.`,
                    );
                    return current;
                }

                // Create new properties object with renamed key
                const newProperties: any = {};
                Object.keys(properties).forEach((key) => {
                    if (key === propertyToRename) {
                        newProperties[newName] = propertySchema;
                    } else {
                        newProperties[key] = properties[key];
                    }
                });

                return {
                    ...current,
                    properties: newProperties,
                };
            },
        );

        onSchemaUpdate(updatedSchema);
        setOpenRenameProperty(false);
        setPropertyToRename("");
    };

    const handleAddItem = (state: DialogState) => {
        const newItem: JsonSchema = {
            type: state.type as any,
            ...(state.title && { title: state.title }),
            ...(state.description && { description: state.description }),
        };

        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => ({
                ...current,
                items: newItem,
            }),
        );

        onSchemaUpdate(updatedSchema);
        setOpenAddItem(false);
        setItemDialogState(INITIAL_DIALOG_STATE);
    };

    const handleRemoveItem = () => {
        const updatedSchema = updateSchemaAtPath(
            schema,
            selectedPath,
            (current: any) => ({
                ...current,
                items: undefined,
            }),
        );
        onSchemaUpdate(updatedSchema);
    };

    const handleCopySchema = async () => {
        const schemaJson = JSON.stringify(selectedSchema, null, 2);
        try {
            await navigator.clipboard.writeText(schemaJson);
        } catch (err) {
            console.error("Failed to copy schema:", err);
        }
    };

    return (
        <Stack direction="column" rowGap={2} sx={{ minWidth: 0 }}>
            <Box>
                <Typography variant="h5" sx={{ marginBottom: 2 }}>
                    {selectedSchema.title}
                </Typography>
            </Box>

            <Stack direction="column" rowGap={1}>
                <TypeSelectorField
                    schema={selectedSchema}
                    onUpdate={(updates) => {
                        if (updates.type) {
                            handleTypeChange(updates.type as string);
                        }
                    }}
                    disabled={isCurrentNodeLocked}
                />
            </Stack>

            <Stack direction="column" rowGap={1}>
                <AcxMainTextField
                    id="detail-title"
                    labelText="Title"
                    placeholderText="Schema title"
                    value={selectedSchema.title || ""}
                    onChange={(e) => handleUpdate({ title: e.target.value })}
                    fullWidth
                    isDisabled={isCurrentNodeLocked}
                />

                <AcxMainTextField
                    id="detail-description"
                    labelText="Description"
                    placeholderText="Schema description"
                    value={selectedSchema.description || ""}
                    onChange={(e) =>
                        handleUpdate({ description: e.target.value })
                    }
                    multiline
                    fullWidth
                    rowsMin={3}
                    isDisabled={isCurrentNodeLocked}
                />

                {selectedSchema.type === "string" && (
                    <StringFieldsEditor
                        schema={selectedSchema}
                        onUpdate={handleUpdate}
                        disabled={isCurrentNodeLocked}
                    />
                )}

                {(selectedSchema.type === "number" ||
                    selectedSchema.type === "integer") && (
                    <NumberIntegerFieldsEditor
                        schema={selectedSchema}
                        onUpdate={handleUpdate}
                        disabled={isCurrentNodeLocked}
                    />
                )}
            </Stack>

            {selectedSchema.type === "array" && (
                <ArrayItemsEditor
                    schema={selectedSchema}
                    onRemoveItem={handleRemoveItem}
                    onOpenAddDialog={() => setOpenAddItem(true)}
                    isItemLocked={() => isPathLocked([...selectedPath, "items"])}
                />
            )}

            {selectedSchema.type === "object" && (
                <ObjectPropertiesEditor
                    schema={selectedSchema}
                    onRemoveProperty={handleRemoveProperty}
                    onRenameProperty={handleOpenRenameProperty}
                    onOpenAddDialog={() => setOpenAddProperty(true)}
                    isPropertyLocked={(propName) =>
                        isPathLocked([...selectedPath, propName])
                    }
                />
            )}

            <Box sx={{ minWidth: 0 }}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={3}
                    mb={1}
                >
                    <Typography variant="h6">Schema Preview</Typography>
                    <IconButton
                        size="small"
                        onClick={handleCopySchema}
                        title="Copy schema to clipboard"
                        sx={{ fontSize: "16px" }}
                    >
                        <CopyIcon fontSize="inherit" />
                    </IconButton>
                </Stack>
                <Box sx={{ overflowX: "auto", maxWidth: "100%" }}>
                    <pre
                        style={{
                            backgroundColor: "#f5f5f5",
                            padding: "12px",
                            borderRadius: "4px",
                            overflow: "auto",
                            fontSize: "12px",
                            maxHeight: "300px",
                            margin: 0,
                        }}
                    >
                        {JSON.stringify(selectedSchema, null, 2)}
                    </pre>
                </Box>
            </Box>

            <AddSchemaDialog
                open={openAddProperty}
                onClose={() => setOpenAddProperty(false)}
                onAdd={handleAddProperty}
                dialogState={propertyDialogState}
                onDialogStateChange={setPropertyDialogState}
                title="Add Property"
                showNameField={true}
            />

            <AddSchemaDialog
                open={openAddItem}
                onClose={() => setOpenAddItem(false)}
                onAdd={handleAddItem}
                dialogState={itemDialogState}
                onDialogStateChange={setItemDialogState}
                title="Add Array Item"
                showNameField={false}
            />

            <RenamePropertyDialog
                open={openRenameProperty}
                onClose={() => setOpenRenameProperty(false)}
                onRename={handleRenameProperty}
                currentName={propertyToRename}
                existingPropertyNames={
                    selectedSchema.type === "object"
                        ? Object.keys(selectedSchema.properties || {})
                        : []
                }
            />
        </Stack>
    );
}
